import { useQueryParams } from '@calendly/hooks'
import moment from 'moment-timezone'
import { useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { pushQueryParams } from 'actions/core/navigation'
import { timezoneSelector } from 'selectors/current_user'

import {
  DATE_RANGES_WITH_UNITS,
  ROUTING_DATE_RANGES_WITH_UNITS,
  ROUTING_DATE_RANGE_PERIODS,
  AVAILABILITY_SUMMARY_PATH,
  PAST_TAB_PATH,
  PAST_TAB_PATH_ROOT,
  ROUTING_PATH,
  UPCOMING_TAB_PATH
} from '../constants'
import { formatTimestampAsString, getDefaultTimes, getSelectedDateRanges, getSelectedRoutingDateRanges } from '../helpers'
import { getFutureDateParamsSelector, getPastDateParamsSelector } from '../selectors'
import { setPastDateRange, setFutureDateRange, setAvailabilityDateRange, setRoutingDateRange } from '../slice'

const isDateInPast = (date, timezone) => moment.tz(date, timezone).isSameOrBefore(moment.tz(timezone).startOf('hour'))
const isDateInFuture = (date, timezone) => moment.tz(date, timezone).isAfter(moment.tz(timezone).startOf('hour'))

export const useDateParams = () => {
  const dispatch = useDispatch()
  const futureDateParams = useSelector(getFutureDateParamsSelector)
  const pastDateParams = useSelector(getPastDateParamsSelector)
  const { start: unixStart, end: unixEnd, ...queryParams } = useQueryParams()
  const timezone = useSelector(timezoneSelector) || moment.tz.guess(true)
  const location = useLocation()

  const start = formatTimestampAsString(unixStart, timezone)
  const end = formatTimestampAsString(unixEnd, timezone)

  const queryParamsString = JSON.stringify(queryParams)
  const setDateParams = useCallback((params = {}, setState = () => {}) => {
    // get dateParams with unix timestamp values
    const { range, ...dateParams } = params
    const unixDateParams = Object.keys(dateParams).reduce((acc, key) => ({
      ...acc,
      [key]: moment(dateParams[key]).unix()
    }), {})

    dispatch(pushQueryParams({ ...unixDateParams, ...JSON.parse(queryParamsString), range }))
    dispatch(setState(dateParams))
  }, [dispatch, queryParamsString])

  const setFutureDateParams = useCallback(() => {
    const updateParams = (params) => setDateParams(params, setFutureDateRange)

    if (start && end && isDateInFuture(start, timezone)) {
      updateParams({ start, end })
    } else if (futureDateParams?.start && futureDateParams?.end && isDateInFuture(futureDateParams?.start, timezone)) {
      updateParams({ start: futureDateParams.start, end: futureDateParams.end })
    } else {
      const { future } = getDefaultTimes(timezone)
      updateParams(future)
    }
  }, [start, end, timezone, futureDateParams.start, futureDateParams.end, setDateParams])

  const setPastDateParams = useCallback(() => {
    const updateParams = (params) => setDateParams(params, setPastDateRange)

    if (start && end && isDateInPast(end, timezone)) {
      updateParams({ start, end })
    } else if (pastDateParams?.start && pastDateParams?.end && isDateInPast(pastDateParams?.end, timezone)) {
      updateParams({ start: pastDateParams.start, end: pastDateParams.end })
    } else {
      const { past } = getDefaultTimes(timezone)
      updateParams(past)
    }
  }, [start, end, timezone, pastDateParams.start, pastDateParams.end, setDateParams])

  const setAvailabilityDateParams = useCallback(() => {
    const updateParams = (params) => setDateParams(params, setAvailabilityDateRange)

    const range = queryParams.range && Array.isArray(queryParams.range) ? queryParams.range[-1] : queryParams.range
    if (range && Object.keys(DATE_RANGES_WITH_UNITS).includes(range?.toUpperCase())) {
      const [rangeStart, rangeEnd] = getSelectedDateRanges(range, timezone)
      updateParams({ start: rangeStart, end: rangeEnd, range })
    } else if (
      range === 'custom'
      && start && end
      && moment(end).diff(moment(start), 'year') === 0
    ) {
      updateParams({ start, end, range })
    } else {
      const defaultRangeParam = 'this_week'
      const [startTime, endTime] = getSelectedDateRanges(defaultRangeParam, timezone)

      updateParams({ start: startTime, end: endTime, range: defaultRangeParam })
    }
  }, [queryParams.range, start, end, setDateParams, timezone])

  const setRoutingDateParams = useCallback(() => {
    const updateParams = (params) => setDateParams(params, setRoutingDateRange)

    const range = queryParams.range && Array.isArray(queryParams.range) ? queryParams.range[-1] : queryParams.range
    if (range && Object.keys(ROUTING_DATE_RANGES_WITH_UNITS).includes(range?.toUpperCase())) {
      const [rangeStart, rangeEnd] = getSelectedRoutingDateRanges(range, timezone)
      updateParams({ start: rangeStart, end: rangeEnd, range })
    } else if (
      range === ROUTING_DATE_RANGE_PERIODS.FIXED
      && start && end
      && moment(end).diff(moment(start), 'year') === 0
    ) {
      updateParams({ start, end, range })
    } else {
      updateParams({ range: ROUTING_DATE_RANGE_PERIODS.ALL_TIME })
    }
  }, [queryParams.range, start, end, setDateParams, timezone])

  useEffect(() => {
    if (location.pathname === PAST_TAB_PATH || location.pathname === PAST_TAB_PATH_ROOT) {
      setPastDateParams()
    } else if (location.pathname === UPCOMING_TAB_PATH) {
      setFutureDateParams()
    } else if (location.pathname === AVAILABILITY_SUMMARY_PATH) {
      setAvailabilityDateParams()
    } else if (location.pathname === ROUTING_PATH) {
      setRoutingDateParams()
    }
  }, [location.pathname, setFutureDateParams, setPastDateParams, setAvailabilityDateParams, setRoutingDateParams])
}
